/*
  <besigner-3d-scene> — scroll-linked 3D background for the 123besigner theme.
  This small file handles scroll progress and visibility. The heavy 3D engine
  (besigner-3d.js) is only downloaded after the page has finished loading, and
  never on data-saver connections. If WebGL is unavailable the section keeps its
  styled backdrop and copy, so nothing breaks.
*/

const clamp = (v) => Math.min(1, Math.max(0, v));
const UNLOAD_DELAY = 4000; // free GPU memory when a scene has been off-screen this long

class Besigner3DScene extends HTMLElement {
  connectedCallback() {
    this.canvas = this.querySelector('canvas');
    if (!this.canvas) return;
    this.pinned = this.hasAttribute('data-pinned');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.visible = false;
    this.ticking = false;

    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    };
    this.onVisibility = () => this.syncActive();
    this.onPointer = (e) => {
      if (!this.scene) return;
      this.scene.setPointer((e.clientX / window.innerWidth) * 2 - 1, (e.clientY / window.innerHeight) * 2 - 1);
    };
    this.onLost = () => {
      this.teardown();
      this.classList.add('b3d--static');
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    document.addEventListener('visibilitychange', this.onVisibility);
    this.addEventListener('b3d:lost', this.onLost);
    if (window.matchMedia('(pointer: fine)').matches && !this.reducedMotion) {
      window.addEventListener('pointermove', this.onPointer, { passive: true });
    }

    this.io = new IntersectionObserver(
      (entries) => {
        this.visible = entries[entries.length - 1].isIntersecting;
        if (this.visible) {
          clearTimeout(this.unloadTimer);
          this.boot();
        } else if (this.scene) {
          this.unloadTimer = setTimeout(() => this.teardown(), UNLOAD_DELAY);
        }
        this.syncActive();
      },
      { rootMargin: '25% 0px' }
    );
    this.io.observe(this);
    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    window.removeEventListener('pointermove', this.onPointer);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.removeEventListener('b3d:lost', this.onLost);
    this.io?.disconnect();
    clearTimeout(this.unloadTimer);
    this.teardown();
  }

  progress() {
    const r = this.getBoundingClientRect();
    const vh = window.innerHeight || 1;
    if (this.pinned) {
      const travel = r.height - vh;
      return travel > 0 ? clamp(-r.top / travel) : clamp(-r.top / vh);
    }
    // Scroll through normally: 0 when the section first comes into view (or at page
    // load if it's already on screen, like a hero), 1 when it has scrolled off the top.
    const entry = Math.max(0, Math.min(vh, r.top + window.scrollY));
    return clamp((entry - r.top) / (entry + r.height));
  }

  startsOnScreen() {
    const r = this.getBoundingClientRect();
    return r.top + window.scrollY < (window.innerHeight || 1) * 0.9;
  }

  update() {
    const p = this.progress();
    this.style.setProperty('--b3d-p', p.toFixed(4));
    this.scene?.setProgress(p);
  }

  syncActive() {
    this.scene?.setActive(this.visible && !document.hidden);
  }

  /* If the section marks a slot for the model ([data-b3d-anchor]), report where that slot
     sits inside the canvas. Uses layout offsets, so scroll parallax transforms don't skew it. */
  anchorRect() {
    const slot = this.querySelector('[data-b3d-anchor]');
    if (!slot) return null;
    return () => {
      const box = this.canvas?.parentElement?.offsetParent;
      if (!box || !slot.offsetWidth) return null;
      let x = 0;
      let y = 0;
      let node = slot;
      while (node && node !== box) {
        x += node.offsetLeft;
        y += node.offsetTop;
        node = node.offsetParent;
      }
      if (node !== box) {
        const c = this.canvas.getBoundingClientRect();
        const s = slot.getBoundingClientRect();
        return { x: s.left - c.left + s.width / 2, y: s.top - c.top + s.height / 2, w: s.width, h: s.height };
      }
      const wrap = this.canvas.parentElement;
      x -= wrap.offsetLeft + this.canvas.offsetLeft;
      y -= wrap.offsetTop + this.canvas.offsetTop;
      return { x: x + slot.offsetWidth / 2, y: y + slot.offsetHeight / 2, w: slot.offsetWidth, h: slot.offsetHeight };
    };
  }

  canTry() {
    const c = navigator.connection;
    if (c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || ''))) return false;
    return 'IntersectionObserver' in window && 'ResizeObserver' in window;
  }

  boot() {
    if (this.scene || this.loading || this.classList.contains('b3d--static')) return;
    if (!this.canTry()) {
      this.classList.add('b3d--static');
      return;
    }
    this.loading = true;

    const start = () => {
      import(this.dataset.engine)
        .then((mod) => {
          this.loading = false;
          if (!this.isConnected || this.scene || !this.visible) return;
          const d = this.dataset;
          try {
            this.scene = mod.createScene(this.canvas, {
              model: d.model,
              position: d.position,
              spins: parseFloat(d.spins) || 1,
              fadeStart: parseFloat(d.fadeStart),
              fadeEnd: parseFloat(d.fadeEnd),
              fadeIn: this.pinned || this.startsOnScreen() ? 0 : parseFloat(d.fadeIn) || 0,
              mobileY: isNaN(parseFloat(d.mobileY)) ? -0.16 : parseFloat(d.mobileY),
              frameColor: d.frameColor,
              lensColor: d.lensColor,
              accent: d.accent,
              finish: d.finish,
              reducedMotion: this.reducedMotion,
              anchor: this.anchorRect(),
            });
          } catch (err) {
            this.classList.add('b3d--static');
            return;
          }
          this.update();
          this.syncActive();
          requestAnimationFrame(() => this.classList.add('b3d--ready'));
          // web fonts can nudge an anchored model's slot; re-measure once they land
          if (this.querySelector('[data-b3d-anchor]') && document.fonts?.ready) {
            document.fonts.ready.then(() => this.scene?.resize());
          }
        })
        .catch(() => {
          this.loading = false;
          this.classList.add('b3d--static');
        });
    };

    const idle = (fn) =>
      'requestIdleCallback' in window ? window.requestIdleCallback(fn, { timeout: 1500 }) : setTimeout(fn, 250);

    if (document.readyState === 'complete') idle(start);
    else window.addEventListener('load', () => idle(start), { once: true });
  }

  teardown() {
    if (!this.scene) return;
    this.classList.remove('b3d--ready');
    try {
      this.scene.destroy();
    } catch (err) {
      /* already gone */
    }
    this.scene = null;
    // a canvas can't host a second WebGL context after a forced loss, so swap in a fresh one
    const fresh = this.canvas.cloneNode(false);
    fresh.removeAttribute('style');
    this.canvas.replaceWith(fresh);
    this.canvas = fresh;
  }
}

if (!customElements.get('besigner-3d-scene')) {
  customElements.define('besigner-3d-scene', Besigner3DScene);
}
